import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClusterElementViewModel } from 'projects/phdsoft/common/src/lib/models/cluster-element.model';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { AssetManagerFilter } from 'src/app/asset-manager/asset-manager-filter';
import { ClusterService } from 'src/app/cluster/cluster.service';
import { environment } from 'src/environments/environment';
import { Geometries, Origins, ReasonsType } from './critical-area-window.component';
import { ElementInfoCriticalArea, IReasons } from './critical-area.model';
import { RiskLevel } from 'src/app/automatic-points/risk-shield/risk-shield-config.model';
import { Observable, of, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RiskLevelItem } from '@app/admin/pages/risk-setup/types/risk-config.model';
import { executionDay, localDayToUtcIso } from 'src/app/shared/utils/calendar-day.util';

/** What deleting one Critical Area element would do (`CriticalArea/DeletionCheck`). */
export interface AreaDeletionCheck {
  /** The element is the area's last, so the area goes with it. */
  deletesArea: boolean;
  /** Plans still holding the area's planned (Outlined) work. */
  plans: { PlanId: string; Name: string; TargetDate: string; OutlinedItemCount: number }[];
  /** The user holds "Delete Critical Area with planned work" on the asset. */
  canForce: boolean;
}

export interface ICriticalAreaElementDTO {
  Id: string,
  CriticalAreaId?: string,
  ElementInfo?: ElementInfoCriticalArea,
  assetId: string,
  regionId: string,
  regionIds: string[],
  sectionId: string,
  TargetGeometryIds: string[],
  TargetGeometryTypeId: ELEMENTSTYPESIDS,
  Element: any
}
export interface ICriticalAreaDTO {
  id?: string,
  criticalAreaElementIds?: string[],
  assetId: string,
  regionIds?: string[],
  origin: Origins,
  geometry: Geometries,
  reasons?: IReasons[],
  label: string,
  dateStarted: Date,
  dateEnded?: Date,
  TargetGeometryTypeId?: ELEMENTSTYPESIDS,
  riskLevel?: RiskLevel
  riskLevelId?: string
  HighestReason?: IReasons
  area?: number

    // New properties
  CriticalAreaActions?: ICriticalAreaAction[];
  RepairIds?: string[];
  InspectionIds?: string[];
}

export interface ICriticalAreaAction {
  Id: string;
  /** Plan Management's identity for this action in this area; the 3D window executes by it. */
  ActionOccurrenceId?: string;
  PlanId: string;
  Name: string;
  TypeId: string;
  MethodId: string;
  Date: Date;
  IsExecuted: boolean;
  AnomalyIds: string[];
  IsImproveInspection?: boolean;
  /** Set when a user changed this action's work to another action; the entry stays. */
  ReplacedAtUtc?: string | null;
  ReplacedByActionOccurrenceId?: string | null;
  /** On the action that replaced another, the occurrence it replaced. */
  ReplacesActionOccurrenceId?: string | null;
}

export interface ICreateCriticalAreasRequestModelDTO {
  criticalArea: ICriticalAreaDTO,

  assetId: string,
  regionId: string,
  sectionIds?: string[],
  elementIds?: string[],

  xMax: number,
  yMax: number,
  zMax: number,

  xMin: number,
  yMin: number,
  zMin: number

}

export interface SectionRisk {
  sectionId: string
  riskLevel: RiskLevel
  riskLevelId: string
  highestReason: IReasons
  criticalAreas: ICriticalAreaDTO[]
}

export interface RegionRisk {
  regionId: string
  riskLevel: RiskLevel
  riskLevelId: string
  highestReason: IReasons
  sections: SectionRisk[]
}

export interface RegionsAndSectionsRisk {
  regions: RegionRisk[]
}

export interface AssetRisk {
  assetId: string;
  riskLevel: RiskLevelItem;
}


@Injectable({
  providedIn: 'root'
})
export class CriticalAreaService {

  public dateSet: Set<string>
  public regionSet: Set<string>

  private readonly criticalAreasChangedSubject = new Subject<void>();
  /**
   * Emits once a critical area save/delete/generation has reached the backend.
   * Risk data fetched before that (AssetManager risk colours, Risk Assessment
   * matrix) is stale from then on and must be fetched again.
   */
  public readonly criticalAreasChanged$ = this.criticalAreasChangedSubject.asObservable();

  constructor(private http: HttpClient,
    private clusterService: ClusterService,
  ) {
    this.dateSet = new Set<string>();
    this.regionSet = new Set<string>();
  }

  getCriticalAreas(regionId: string, assetId: string) {
    let params = new HttpParams();
    params
      .set('regionId', regionId)
      .set('assetId', assetId)
    return this.http.get<{ criticalAreas: ICriticalAreaDTO[], criticalAreaElements: ICriticalAreaElementDTO[] }>(`${environment.API_INSPEC}CriticalArea?regionId=${regionId}&assetId=${assetId}`)
  }

  getCriticalAreasByAssetId(assetId: string) {
    let params = new HttpParams();
    params
      .set('assetId', assetId)
    return this.http.get<{ criticalAreas: ICriticalAreaDTO[], criticalAreaElements: ICriticalAreaElementDTO[] }>(`${environment.API_INSPEC}CriticalArea/GetCriticalAreasByAssetId?assetId=${assetId}`)
  }

  getCriticalArea(criticalAreaId: string) {
    return this.http.get<ICriticalAreaDTO>(`${environment.API_INSPEC}CriticalArea/${criticalAreaId}`)
  }

  // getAllCriticalAreas(assetId: String) {
  //   return this.http.get<any>(environment.API_INSPEC + `CriticalArea/GetAllCriticalAreas/${assetId}`)
  // }

  // putCriticalArea(criticalArea: ICriticalAreaDTO, criticalAreaElement: ICriticalAreaElementDTO) {
  //   // const params = new HttpParams()
  //   // .set('id', criticalArea.id)
  //   // .set('assetId', criticalArea.assetId)
  //   // .set('geometry', criticalArea.geometry)
  //   // .set('origin', criticalArea.origin)
  //   // .set('reasons', criticalArea.reasons)
  //   // .set('dateEnded', criticalArea.dateEnded.toJSON())
  //   // .set('dateStarted', criticalArea.dateStarted.toJSON())
  //   const criticalAreaSaveDTO = {
  //     criticalArea,
  //     criticalAreaElement
  //   }

  //   return this.http.put<any>(environment.API_INSPEC + `CriticalArea`, criticalAreaSaveDTO)
  // }

  postCriticalArea(criticalArea: ICriticalAreaDTO, criticalAreaElement: ICriticalAreaElementDTO[]) {
    // const params = new HttpParams()
    // .set('id', criticalArea.id)
    // .set('assetId', criticalArea.assetId)
    // .set('geometry', criticalArea.geometry)
    // .set('origin', criticalArea.origin)
    // .set('reasons', criticalArea.reasons)
    // .set('dateStarted', criticalArea.dateStarted.toJSON())
    // if(criticalArea.dateEnded?.toJSON()){
    //   params.set('dateEnded', criticalArea.dateEnded.toJSON())
    // }
    const criticalAreaSaveDTO = {
      criticalArea,
      criticalAreaElement
    }

    this.manageCriticalAreaCluster(criticalArea.dateStarted, criticalAreaElement[0].regionId, criticalArea, criticalAreaElement)


    return this.http.post(`${environment.API_INSPEC}CriticalArea`, criticalAreaSaveDTO)
      .pipe(tap(() => this.criticalAreasChangedSubject.next()))

  }

  generateCriticalAreaByVolume(request: ICreateCriticalAreasRequestModelDTO) {
    var result = this.http.post<string[]>(`${environment.API_INSPEC}CriticalArea/GenerateCriticalAreaByVolume`, request)
      .pipe(tap(() => this.criticalAreasChangedSubject.next()))
    return result;
  }

  deleteCriticalArea(criticalAreaId: string, assetId: string, criticalArea: ICriticalAreaDTO, criticalAreaElement: ICriticalAreaElementDTO[], count: number) {
    this.getCriticalArea(criticalArea.id).subscribe(criticalAreaToBeDeleted => {
      if (criticalAreaToBeDeleted) {
        this.regenerateCriticalAreaClusterIfNeededWhenDeleted(criticalArea, criticalAreaElement, count)
        this.http.delete<boolean>(`${environment.API_INSPEC}CriticalArea/DeleteCriticalArea?criticalAreaId=${criticalArea.id}&assetId=${assetId}`).toPromise()
          .finally(() => this.criticalAreasChangedSubject.next())
      }
    })
  }

  /** Asked before anything is removed, so a refused delete leaves the scene as it was. */
  checkAreaDeletion(criticalAreaElementId: string, assetId: string): Observable<AreaDeletionCheck> {
    return this.http.get<AreaDeletionCheck>(`${environment.API_INSPEC}CriticalArea/DeletionCheck`, {
      params: { criticalAreaElementId, assetId }
    })
  }

  /** `force`: the user confirmed deleting an area with planned work, which cancels it. */
  deleteCriticalAreaElement(criticalAreaId: string, assetId: string, criticalArea: ICriticalAreaDTO, criticalAreaElements: ICriticalAreaElementDTO[], count: number, force = false) {
    this.getCriticalArea(criticalArea.id).subscribe(criticalAreaToBeDeleted => {
      if (criticalAreaToBeDeleted) {
        const deletes: Promise<boolean>[] = []
        for (var element of criticalAreaElements) {
          deletes.push(this.http.delete<boolean>(`${environment.API_INSPEC}CriticalArea/DeleteCriticalAreaElement?criticalAreaElementId=${element.Id}&assetId=${assetId}${force ? '&force=true' : ''}`).toPromise())
        }
        Promise.all(deletes).finally(() => this.criticalAreasChangedSubject.next())
        this.regenerateCriticalAreaClusterIfNeededWhenDeleted(criticalArea, criticalAreaElements, count)
      }
    })
  }

  manageCriticalAreaCluster(dateStarted: Date, regionId: string, criticalArea: ICriticalAreaDTO, criticalAreaElement: ICriticalAreaElementDTO[]) {
    let criticalAreaDateStarted = dateStarted.toJSON().split('T')[0];


    if (this.regionSet.has(regionId)) {
      if (!this.dateSet.has(criticalAreaDateStarted)) {
        this.dateSet.add(criticalAreaDateStarted)
        this.regenerateCriticalAreaClusterIfNeeded(criticalArea, criticalAreaElement)
      }
    } else {
      this.regionSet.clear()
      this.dateSet.clear()
      this.regionSet.add(regionId)
      this.dateSet.add(criticalAreaDateStarted)
      this.regenerateCriticalAreaClusterIfNeeded(criticalArea, criticalAreaElement)
    }

  }

  regenerateCriticalAreaClusterIfNeeded(criticalArea: ICriticalAreaDTO, criticalAreaElement: ICriticalAreaElementDTO[]) {
    const clusterElementViewModel: ClusterElementViewModel = {
      ClusterType: "Critical Area",
      AssetId: criticalArea.assetId,
      Dates: Array(criticalArea.dateStarted),
      InspectionTypeId: "",
      RegionIds: criticalAreaElement[0].regionIds
    }

    this.clusterService.RegenerateClustersIfNeeded(Array(clusterElementViewModel)).toPromise()
  }

  regenerateCriticalAreaClusterIfNeeded2(assetId: string, regionIds: string[], dates: Date[]): Observable<boolean[]> {
    const clusterElementViewModel: ClusterElementViewModel = {
      ClusterType: "Critical Area",
      AssetId: assetId,
      Dates: dates,
      InspectionTypeId: "",
      RegionIds: regionIds
    }
    
    return this.clusterService.RegenerateClustersIfNeeded(Array(clusterElementViewModel))
  }

  regenerateCriticalAreaClusterIfNeededWhenDeleted(criticalArea: ICriticalAreaDTO, criticalAreaElement: ICriticalAreaElementDTO[], count: number) {
    const clusterElementViewModel: ClusterElementViewModel = {
      ClusterType: "Critical Area",
      AssetId: criticalArea.assetId,
      Dates: Array(criticalArea.dateStarted),
      InspectionTypeId: "",
      RegionIds: criticalAreaElement[0].regionIds
    }
    this.dateSet.clear()
    this.regionSet.clear()
    if (count == 0) {
      this.clusterService.RegenerateClustersIfNeededWhenDeleted(clusterElementViewModel).toPromise()
    }
  }

  getRegionsAndSectionsRiskColor(regionIds: string[], assetId: string) {
    return this.http.post<RegionsAndSectionsRisk>(`${environment.API_INSPEC}CriticalArea/GetRegionsAndSectionsRiskColor?assetId=${assetId}`, regionIds)
  }

  getRegionsAndSectionsByAssetIdRiskColor(assetId: string) {
    return this.http.post<RegionsAndSectionsRisk>(`${environment.API_INSPEC}CriticalArea/GetRegionsAndSectionsRiskColor?assetId=${assetId}`, null)
  }

  getRedCriticalAreaRange(customerId: string, assetType: number) {
    var result = this.http.get<number[]>(`${environment.API_INSPEC}CriticalArea/GetRedCriticalAreaRange?customerId=${customerId}&assetType=${assetType}`)
    return result;
  }

  getRegionsAndSectionsRisk(regionIds: string[], assetId: string) {
    return this.http.post<RegionsAndSectionsRisk>(`${environment.API_INSPEC}CriticalArea/GetRegionsAndSectionsRisk?assetId=${assetId}`, regionIds)
  }

  saveCriticalArea(criticalArea: ICriticalAreaDTO) {
    return this.http.post(`${environment.API_INSPEC}CriticalArea/SaveCriticalArea`, criticalArea)
      .pipe(tap(() => this.criticalAreasChangedSubject.next()))
  }

  getCriticalAreasCount(ids: string[], isExecuted: boolean, actionId: string): Observable<number> {
    let params = new HttpParams()
      .set('isExecuted', isExecuted.toString())
      .set('actionId', actionId);

    return this.http.post<number>(
      `${environment.API_INSPEC}CriticalArea/GetCriticalAreasCount`,
      ids,
      { params }
    );
  }

  /**
   * Starts the action's anomalies and, for a Direct action, records its execution in Plan
   * Management. An action Plan Management created carries no plan id, so the asset, the
   * area and the action itself are sent too.
   */
  executeActions(anomalyIds: string[], referenceDate: string, assetId: string, criticalAreaId: string, action: ICriticalAreaAction) {
    let params = new HttpParams()
      .set('planId', action.PlanId ?? '')
      .set('referenceDate', referenceDate)
      // The day the action counts as executed: the date 3D shows, or today when that is ahead.
      .set('executedOn', localDayToUtcIso(executionDay(referenceDate)))
      .set('assetId', assetId)
      .set('criticalAreaId', criticalAreaId)
      .set('actionId', action.Id ?? '');
    if (action.ActionOccurrenceId) {
      params = params.set('actionOccurrenceId', action.ActionOccurrenceId);
    }
    if (action.Date) {
      params = params.set('actionDate', new Date(action.Date).toISOString());
    }
    return this.http.post<boolean>(`${environment.API_INSPEC}CriticalArea/ExecuteActions`, anomalyIds, { params })
  }

  GetAllAssetsRisk(assetIds: string[]): Observable<AssetRisk[]> {
    if(assetIds.length === 0) {
      return of([]);
    }
    
    try {
      return this.http.post<AssetRisk[]>(`${environment.API_INSPEC}CriticalArea/GetAllAssetsRisk`, assetIds)
    } catch (error) {
      console.error('Error getting all assets risk:', error);
      return of([]);
    }
  }
}
